import { useCallback, useState } from 'react';

import { getErrorMessage } from '@givebacks/utils';

import { PRINTTY_ELEMENT_CLASS_ID } from '../constants';
import { UsePrintty } from '../types';
import { useCreatePrinttyTarget } from './useCreateTarget';

/**
 * Hook to handle printing functionality
 * Uses the useCreatePrinttyTarget hook to create a target for printing
 */
const usePrint = ({
  HeaderElement,
  HeaderInfoBoxElement,
  orientation,
  debug,
  onClose,
  headerProps,
}: UsePrintty = {}) => {
  const [printError, setPrintError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { createTarget, targetRef, cleanupTargetFrame, targetReactRef, setTargetReactRef } =
    useCreatePrinttyTarget({
      HeaderElement,
      HeaderInfoBoxElement,
      orientation,
      debug,
      onClose,
      headerProps,
    });

  /** Calls print on the window after a small que timeout */
  const startPrintJob = useCallback(async (printFrame: HTMLIFrameElement) => {
    const printWindow = printFrame.contentWindow;

    /**
     * This is needed to move the print task to the back of the callstack
     * this helps with ensuring the print target has loaded the content
     *
     * While this actually is solved with the performance improvements done in `useCreateTarget.createTarget`
     * this timeout could prevent a race condition in the the future if there are performance regressions
     */
    setTimeout(() => {
      printWindow?.print();
    }, 500);
  }, []);

  /**
   * Attaches a cleanup function to the printFrame.
   *
   * Cleans up the print frame
   * and resets loading states and calls onClose
   */
  const handleAfterPrint = useCallback(
    async (printFrame: HTMLIFrameElement) => {
      const printWindow = printFrame.contentWindow;
      if (!printWindow) return;
      return (printWindow.onafterprint = () => {
        cleanupTargetFrame();
        setIsLoading(false);
        onClose?.();
      });
    },
    [cleanupTargetFrame, onClose]
  );

  /**
   * Handles the print event
   */
  const handlePrint = useCallback(async () => {
    setPrintError(null);
    setIsLoading(true);
    try {
      const printFrame = await createTarget();

      await handleAfterPrint(printFrame);

      await startPrintJob(printFrame);
    } catch {
      setPrintError(getErrorMessage('Error printing'));
      setIsLoading(false);
    }
  }, [createTarget, handleAfterPrint, startPrintJob]);

  return {
    targetRef,
    print: handlePrint,
    cleanupTargetFrame,
    isLoading,
    error: printError,
    targetReactRef,
    setTargetReactRef,
    elementClassId: PRINTTY_ELEMENT_CLASS_ID,
  };
};

export { usePrint };
